/**
 * =============================================================================
 * Hook + Context: useAffiliateAuth
 * Versão: 2.0.0 — Modelo N:N (uma pessoa, várias adesões/programas)
 *
 * Mudanças desde v1.x:
 *   - A tabela `affiliates` perdeu `status`, `program_id`, `available_balance`
 *     e `total_*`. Tudo isso vive agora em `affiliate_program_memberships`.
 *   - O painel passa a operar com a "pessoa" (affiliate) + lista de
 *     `memberships`. Não há mais um status global por afiliado: a regra é
 *     "se houver pelo menos uma adesão ativa, o afiliado pode entrar".
 *   - `affiliate.status`, `available_balance` e `total_*` continuam expostos
 *     no objeto, mas são DERIVADOS de memberships (somatório / status mais
 *     prioritário) — para que componentes legados continuem funcionando.
 *
 * Decisão de UX (Fase 3):
 *   - Sem seletor de programa no header. Os dados de cada programa aparecem
 *     em telas próprias (KPIs/Comissões/Saques separam internamente).
 * =============================================================================
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { affiliateLoginPath, getLastAffiliateTenantSlug } from '@/lib/affiliate-urls';

const log = createLogger('AffiliateAuth');

// -----------------------------------------------------------------------------
// Tipos
// -----------------------------------------------------------------------------
export type AffiliateStatus = 'pending' | 'active' | 'suspended' | 'banned' | 'rejected' | 'inactive';

/** Adesão de um afiliado a um programa. */
export interface AffiliateMembershipLite {
  id: string;
  program_id: string;
  status: AffiliateStatus;
  available_balance: number;
  total_earned: number;
  total_paid: number;
  total_clicks: number;
  total_referrals: number;
  total_conversions: number;
  total_points: number;
}

/**
 * Registro do afiliado no painel.
 * Os campos agregados (status/saldos/contadores) são derivados de
 * `memberships`, não estão na tabela `affiliates`.
 */
export interface AffiliateRecord {
  id: string;
  tenant_id: string;
  full_name: string;
  email: string;
  avatar_url: string | null;

  /** Lista de adesões (uma por programa) ordenada por created_at asc. */
  memberships: AffiliateMembershipLite[];

  /** Status "global" derivado: 'active' se houver ≥1 adesão ativa. */
  status: AffiliateStatus;
  /** Somatório de saldos disponíveis em todas as adesões. */
  available_balance: number;
  total_earned: number;
  total_paid: number;
  total_clicks: number;
  total_referrals: number;
  total_conversions: number;
  total_points: number;
}

interface AffiliateAuthContextValue {
  session: Session | null;
  user: User | null;
  /** Afiliado ativo no painel (ou null se usuário não é afiliado). */
  affiliate: AffiliateRecord | null;
  /** Lista de afiliados vinculados ao usuário (multi-tenant). */
  availableAffiliates: AffiliateRecord[];
  loading: boolean;
  ready: boolean;

  signUp: (input: SignUpInput) => Promise<SignUpResult>;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signOut: () => Promise<void>;
  switchAffiliate: (affiliateId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export interface SignUpInput {
  tenant_slug: string;
  program_slug: string;
  email: string;
  password: string;
  full_name: string;
  phone?: string;
  document_number?: string;
  document_type?: 'cpf' | 'cnpj';
  terms_accepted: boolean;
  /** Token opcional de convite privado (fluxo invite_only). */
  invite_token?: string;
}

export interface SignUpResult {
  success: boolean;
  affiliate_id?: string;
  status?: AffiliateStatus;
  auto_approved?: boolean;
  message?: string;
  error?: string;
  /** Códigos do plano v2: 'email_already_exists' | 'already_member' */
  code?: string;
  /** Quando 409: rota para onde mandar o usuário (login + next=/vincular-programa/...) */
  redirect_to?: string;
}

export interface SignInResult {
  success: boolean;
  can_access?: boolean;
  redirect_to?: string;
  message?: string;
  affiliate?: AffiliateRecord;
  error?: string;
  /** Código de negócio retornado pela edge function (ex.: 'pending_invite'). */
  code?: string;
}


// -----------------------------------------------------------------------------
// Helpers de derivação (status global + somatórios)
// -----------------------------------------------------------------------------
const STATUS_PRIORITY: AffiliateStatus[] = [
  'active',
  'pending',
  'suspended',
  'banned',
  'rejected',
  'inactive',
];

function deriveGlobalStatus(memberships: AffiliateMembershipLite[]): AffiliateStatus {
  if (!memberships.length) return 'pending';
  for (const s of STATUS_PRIORITY) {
    if (memberships.some((m) => m.status === s)) return s;
  }
  return memberships[0].status;
}

function buildAffiliateRecord(
  base: { id: string; tenant_id: string; full_name: string; email: string; avatar_url: string | null },
  memberships: AffiliateMembershipLite[],
): AffiliateRecord {
  const sum = (key: keyof AffiliateMembershipLite) =>
    memberships.reduce((acc, m) => acc + (Number(m[key as keyof AffiliateMembershipLite]) || 0), 0);

  return {
    ...base,
    memberships,
    status: deriveGlobalStatus(memberships),
    available_balance: sum('available_balance'),
    total_earned: sum('total_earned'),
    total_paid: sum('total_paid'),
    total_clicks: sum('total_clicks'),
    total_referrals: sum('total_referrals'),
    total_conversions: sum('total_conversions'),
    total_points: sum('total_points'),
  };
}

// -----------------------------------------------------------------------------
// Context
// -----------------------------------------------------------------------------
const AffiliateAuthContext = createContext<AffiliateAuthContextValue | undefined>(undefined);

const STORAGE_KEY = 'currentAffiliateId';

// -----------------------------------------------------------------------------
// Provider
// -----------------------------------------------------------------------------
export function AffiliateAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [availableAffiliates, setAvailableAffiliates] = useState<AffiliateRecord[]>([]);
  const [affiliate, setAffiliate] = useState<AffiliateRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  const userIdRef = useRef<string | null>(null);
  const fetchingRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Carrega afiliados + memberships do usuário
  // ---------------------------------------------------------------------------
  const loadAffiliates = useCallback(async (_userId: string) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    try {
      // 1) Busca os "afiliados" do usuário via RPC SECURITY DEFINER.
      //    SELECT direto na tabela `affiliates` foi bloqueado pelo hardening RLS
      //    (etapa de segurança server-side v1.0.0).
      const { data: dirData, error } = await supabase.rpc(
        'list_my_affiliate_directory',
      );

      if (error) {
        log.error('Erro ao buscar afiliados (RPC)', error);
        setAvailableAffiliates([]);
        setAffiliate(null);
        return;
      }

      const baseList = (dirData as Array<{
        id: string;
        tenant_id: string;
        full_name: string | null;
        email: string | null;
        avatar_url: string | null;
      }> | null) ?? [];

      if (baseList.length === 0) {
        setAvailableAffiliates([]);
        setAffiliate(null);
        return;
      }

      // 2) Carrega memberships via RPC SECURITY DEFINER (todos os afiliados do user).
      const { data: memData, error: memErr } = await supabase.rpc(
        'list_my_affiliate_memberships',
        { p_affiliate_id: null },
      );

      if (memErr) {
        log.error('Erro ao carregar memberships (RPC)', memErr);
      }

      const memRows = (memData as Array<{
        id: string;
        affiliate_id: string;
        program_id: string;
        status: AffiliateStatus;
        available_balance: number | string | null;
        total_earned: number | string | null;
        total_paid: number | string | null;
        total_clicks: number | string | null;
        total_referrals: number | string | null;
        total_conversions: number | string | null;
        total_points: number | string | null;
      }> | null) ?? [];

      const memByAffiliate = new Map<string, AffiliateMembershipLite[]>();
      for (const m of memRows ?? []) {
        const list = memByAffiliate.get(m.affiliate_id) ?? [];
        list.push({
          id: m.id,
          program_id: m.program_id,
          status: m.status as AffiliateStatus,
          available_balance: Number(m.available_balance ?? 0),
          total_earned: Number(m.total_earned ?? 0),
          total_paid: Number(m.total_paid ?? 0),
          total_clicks: Number(m.total_clicks ?? 0),
          total_referrals: Number(m.total_referrals ?? 0),
          total_conversions: Number(m.total_conversions ?? 0),
          total_points: Number(m.total_points ?? 0),
        });
        memByAffiliate.set(m.affiliate_id, list);
      }

      const list: AffiliateRecord[] = baseList.map((row) =>
        buildAffiliateRecord(row, memByAffiliate.get(row.id) ?? []),
      );

      setAvailableAffiliates(list);

      // Determinar afiliado ativo: localStorage → primeiro com status active → primeiro
      const savedId = localStorage.getItem(STORAGE_KEY);
      const saved = savedId ? list.find((a) => a.id === savedId) : null;
      const firstActive = list.find((a) => a.status === 'active');
      const chosen = saved ?? firstActive ?? list[0];

      setAffiliate(chosen);
      localStorage.setItem(STORAGE_KEY, chosen.id);
    } finally {
      fetchingRef.current = false;
      setLoading(false);
      setReady(true);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Presença: marca last_login_at / last_seen_at
  // ---------------------------------------------------------------------------
  // Disparado a cada SIGNED_IN (login persistido / refresh inicial), em
  // TOKEN_REFRESHED e periodicamente (heartbeat) enquanto a aba está visível.
  // O servidor resolve o(s) afiliado(s) via auth.uid() — front não envia ID.
  const touchPresence = useCallback(
    async (kind: 'login' | 'heartbeat') => {
      try {
        await (supabase.rpc as any)('touch_affiliate_presence', { p_kind: kind });
      } catch (err) {
        log.warn('touch_affiliate_presence falhou', { kind, err });
      }
    },
    [],
  );

  // ---------------------------------------------------------------------------
  // Listener de auth do Supabase
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        const newUserId = currentSession?.user?.id ?? null;
        log.debug('auth event', { event, newUserId });

        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        // Marca presença em qualquer início/refresh de sessão.
        // INITIAL_SESSION dispara mesmo sem novo login → atualiza last_seen_at.
        if (newUserId) {
          if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
            touchPresence('login');
          } else if (event === 'TOKEN_REFRESHED') {
            touchPresence('heartbeat');
          }
        }

        if (newUserId === userIdRef.current && ready) return;
        userIdRef.current = newUserId;

        if (newUserId) {
          setLoading(true);
          setTimeout(() => loadAffiliates(newUserId), 0);
        } else {
          setAffiliate(null);
          setAvailableAffiliates([]);
          setLoading(false);
          setReady(true);
        }
      },
    );

    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      const userId = existing?.user?.id ?? null;
      userIdRef.current = userId;
      setSession(existing);
      setUser(existing?.user ?? null);

      if (userId) {
        loadAffiliates(userId);
        // Sessão já existia ao montar → marca como login (revisita ao painel).
        touchPresence('login');
      } else {
        setLoading(false);
        setReady(true);
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Heartbeat periódico de presença (60s) enquanto há afiliado e aba visível
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!affiliate?.id) return;

    const HEARTBEAT_MS = 60_000;

    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }
      touchPresence('heartbeat');
    };

    const intervalId = window.setInterval(tick, HEARTBEAT_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') touchPresence('heartbeat');
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [affiliate?.id, touchPresence]);

  // ---------------------------------------------------------------------------
  // signUp via Edge Function affiliate-signup
  // ---------------------------------------------------------------------------
  const signUp = useCallback(async (input: SignUpInput): Promise<SignUpResult> => {
    try {
      // OBS: usamos `fetch` direto via supabase.functions.invoke porém precisamos
      // ler o body MESMO em status != 2xx (HTTP 409 com `code`/`redirect_to`).
      // O `invoke` já preserva o body em `error.context.body` quando o status
      // não é 2xx — fallback para `data` quando 200.
      const { data, error } = await supabase.functions.invoke('affiliate-signup', {
        body: input,
      });

      // Caso 1: HTTP 4xx/5xx (FunctionsHttpError) — ler corpo do erro
      if (error) {
        let body: Partial<SignUpResult> | null = null;
        try {
          // Compatibilidade entre versões do supabase-js:
          // - SDK atual: `error.context` é o próprio objeto Response
          // - SDK antigo: `error.context.response` é o Response
          // - Casos raros: `error.context.body` já vem desserializado
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const ctx: any = (error as any)?.context;

          if (ctx && typeof ctx.json === 'function') {
            // SDK atual: ctx é o Response
            body = await ctx.json();
          } else if (ctx?.response && typeof ctx.response.json === 'function') {
            // SDK antigo: ctx.response é o Response
            body = await ctx.response.json();
          } else if (ctx?.body && typeof ctx.body === 'object') {
            // Body já desserializado em algumas versões
            body = ctx.body as Partial<SignUpResult>;
          } else if (typeof ctx?.body === 'string') {
            // Body como string JSON
            try {
              body = JSON.parse(ctx.body);
            } catch {
              /* corpo não-JSON, ignora */
            }
          }
        } catch {
          /* ignore parse errors — mantém fallback genérico abaixo */
        }
        log.warn('signUp HTTP error', { error, body });
        return {
          success: false,
          error: body?.error ?? body?.message ?? error.message ?? 'Erro ao cadastrar',
          code: body?.code,
          redirect_to: body?.redirect_to,
          message: body?.message,
        };
      }

      if (!data?.success) {
        return {
          success: false,
          error: data?.error ?? data?.message ?? 'Erro ao cadastrar',
          code: data?.code,
          redirect_to: data?.redirect_to,
          message: data?.message,
        };
      }
      return data as SignUpResult;
    } catch (err) {
      log.error('signUp exception', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro inesperado',
      };
    }
  }, []);

  // ---------------------------------------------------------------------------
  // signIn (email/senha) → valida via affiliate-login
  // ---------------------------------------------------------------------------
  const signIn = useCallback(async (email: string, password: string): Promise<SignInResult> => {
    try {
      const { data: signInData, error: signInError } =
        await supabase.auth.signInWithPassword({ email, password });

      if (signInError || !signInData.user) {
        return {
          success: false,
          error: signInError?.message ?? 'Credenciais inválidas',
        };
      }

      // ----------------------------------------------------------------------
      // (Plano B revisado) Adoção restrita: para CADA tenant onde este auth.uid
      // já está vinculado a algum affiliate, tenta adotar eventuais registros
      // órfãos do MESMO tenant (ex: duplicata criada depois pelo admin).
      // NUNCA varre tenants onde o usuário ainda não tem vínculo prévio.
      // ----------------------------------------------------------------------
      try {
        // Pós-login: usa RPC SECURITY DEFINER (SELECT direto na tabela affiliates
        // foi bloqueado pelo hardening RLS).
        const { data: dirData } = await supabase.rpc('list_my_affiliate_directory');
        const linkedRows = (dirData as Array<{ tenant_id: string; email: string | null }> | null) ?? [];

        for (const row of linkedRows) {
          if (!row?.tenant_id || !row?.email) continue;
          const { error: linkErr } = await supabase.rpc(
            'link_affiliate_user_in_tenant',
            {
              p_user_id: signInData.user.id,
              p_email: row.email,
              p_tenant_id: row.tenant_id,
            },
          );
          if (linkErr) {
            log.warn('link_affiliate_user_in_tenant pos-login falhou', {
              tenant_id: row.tenant_id,
              error: linkErr,
            });
          }
        }
      } catch (linkEx) {
        log.warn('Adoção pos-login (exception)', linkEx);
      }

      const { data, error } = await supabase.functions.invoke('affiliate-login', {
        body: { user_id: signInData.user.id },
      });

      if (error || !data?.success) {
        // v3.1.0 — Extrai o corpo JSON do erro da edge function.
        // `supabase.functions.invoke` devolve apenas "non-2xx status code" no
        // `error.message`; a mensagem real (e o `code`) vivem em error.context.
        let payload: any = data ?? null;
        if (!payload && (error as any)?.context) {
          try {
            payload = await (error as any).context.json();
          } catch {
            payload = null;
          }
        }

        log.warn('affiliate-login retornou erro', { error, data: payload });
        return {
          success: false,
          code: payload?.code,
          redirect_to: payload?.redirect_to,
          error:
            payload?.error ??
            payload?.message ??
            error?.message ??
            'Conta de afiliado não encontrada',
        } as SignInResult;
      }

      return data as SignInResult;
    } catch (err) {
      log.error('signIn exception', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro inesperado',
      };
    }
  }, []);

  /**
   * Logout do painel do afiliado.
   *
   * CHANGELOG:
   *   v2.1.0 (2026-05-30) — Hard reload direto para `/afiliado/<slug>/login`.
   *     Antes chamávamos apenas `supabase.auth.signOut()`, o que disparava
   *     SIGNED_OUT no listener global do `useAuth` e redirecionava para `/auth`
   *     (login do SaaS). Agora replicamos o padrão do `useAuth.signOut`:
   *     capturamos o access_token, limpamos storage, disparamos revogação
   *     fire-and-forget e fazemos `window.location.replace` para o login do
   *     afiliado do tenant — vencendo o listener global antes que ele reaja.
   */
  const signOut = useCallback(async () => {
    // 1. Resolver destino: login do afiliado do último tenant conhecido.
    const slug = getLastAffiliateTenantSlug();
    const destination = affiliateLoginPath(slug);

    // 2. Capturar access_token do storage do Supabase antes de limpar.
    let accessToken: string | null = null;
    try {
      const supabasePattern = /^sb-.*-auth-token$/;
      const tokenKey = Object.keys(localStorage).find((k) => supabasePattern.test(k));
      if (tokenKey) {
        const raw = localStorage.getItem(tokenKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          accessToken = parsed?.access_token ?? null;
        }
      }
    } catch (e) {
      log.warn('Falha ao capturar access_token para revogação', e);
    }

    // 3. Limpar chave do afiliado + tokens do Supabase (síncrono).
    try {
      localStorage.removeItem(STORAGE_KEY);
      const supabasePattern = /^sb-.*-auth-token$/;
      Object.keys(localStorage)
        .filter((k) => supabasePattern.test(k))
        .forEach((k) => localStorage.removeItem(k));
    } catch (e) {
      log.warn('Falha ao limpar storage local no signOut', e);
    }

    // 4. Revogação remota fire-and-forget — não bloqueia navegação e não
    //    passa por `supabase.auth.signOut()` (que dispararia SIGNED_OUT
    //    no listener global e provocaria replace para /auth).
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
      const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
      if (accessToken && supabaseUrl && anonKey) {
        fetch(`${supabaseUrl}/auth/v1/logout?scope=local`, {
          method: 'POST',
          headers: {
            apikey: anonKey,
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          keepalive: true,
        }).catch((e) => log.warn('Falha na revogação remota (ignorada)', e));
      }
    } catch (e) {
      log.warn('Exceção ao disparar revogação remota (ignorada)', e);
    }

    // 5. Hard reload — destrói árvore React antes do listener global reagir.
    log.info('signOut afiliado — redirecionando', { destination });
    window.location.replace(destination);
  }, []);

  /**
   * Troca de conta ativa multi-tenant.
   * Persiste no servidor via RPC `set_active_affiliate` (sincroniza
   * `user_active_tenant`) — localStorage é apenas cache de UX.
   */
  const switchAffiliate = useCallback(
    async (affiliateId: string) => {
      const found = availableAffiliates.find((a) => a.id === affiliateId);
      if (!found) {
        log.warn('switchAffiliate: id não encontrado', { affiliateId });
        return;
      }
      try {
        const { error } = await supabase.rpc('set_active_affiliate', {
          p_affiliate_id: affiliateId,
        });
        if (error) {
          log.error('set_active_affiliate falhou', error);
          // Ainda assim atualiza UI local — re-tentaremos no próximo load.
        }
      } catch (err) {
        log.error('set_active_affiliate exception', err);
      }
      setAffiliate(found);
      localStorage.setItem(STORAGE_KEY, found.id);
    },
    [availableAffiliates],
  );

  const refresh = useCallback(async () => {
    if (userIdRef.current) {
      await loadAffiliates(userIdRef.current);
    }
  }, [loadAffiliates]);

  const value: AffiliateAuthContextValue = {
    session,
    user,
    affiliate,
    availableAffiliates,
    loading,
    ready,
    signUp,
    signIn,
    signOut,
    switchAffiliate,
    refresh,
  };

  return (
    <AffiliateAuthContext.Provider value={value}>
      {children}
    </AffiliateAuthContext.Provider>
  );
}

export function useAffiliateAuth(): AffiliateAuthContextValue {
  const ctx = useContext(AffiliateAuthContext);
  if (!ctx) {
    throw new Error('useAffiliateAuth deve ser usado dentro de AffiliateAuthProvider');
  }
  return ctx;
}
